import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { FaUser, FaComment, FaStar, FaEye } from 'react-icons/fa';
import apiService from '../../../services/apiService';
import Modal from '../../../components/Modal';

function BidListPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCollectorModalOpen, setIsCollectorModalOpen] = useState(false);
  const [collectorDetails, setCollectorDetails] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedback, setFeedback] = useState({ rating: 0, comment: '' });
  const [selectedBidId, setSelectedBidId] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedReviews, setSelectedReviews] = useState([]);

  useEffect(() => {
    if (!requestId) {
      toast.error('Invalid request ID');
      navigate('/farmer-waste-management');
      return;
    }
    const fetchBids = async () => {
      try {
        const data = await apiService.get(`/waste/bids/${requestId}`);
        console.log("Fetched data:", data);
        setBids(data);
      } catch (err) {
        console.error('Error fetching bids:', err);
        setError(err);
        toast.error('Failed to load bids');
        navigate('/farmer-waste-management');
      } finally {
        setLoading(false);
      }
    };
    fetchBids();
  }, [requestId, navigate]);

  const handleAcceptBid = async (bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'accepted' });
      toast.success('Bid accepted successfully');
      const updatedBids = bids.map(bid =>
        bid._id === bidId ? { ...bid, status: 'accepted' } : bid
      );
      setBids(updatedBids);
    } catch (err) {
      console.error('Accept bid error:', err);
      toast.error('Failed to accept bid');
    }
  };

  const handleDeclineBid = async (bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'declined' });
      toast.success('Bid declined successfully');
      const updatedBids = bids.map(bid =>
        bid._id === bidId ? { ...bid, status: 'declined' } : bid
      );
      setBids(updatedBids);
      setSelectedBidId(bidId);
      setShowFeedbackModal(true);
    } catch (err) {
      console.error('Decline bid error:', err);
      toast.error('Failed to decline bid');
    }
  };

  const handlePendingBid = async (bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'pending' });
      toast.success('Bid set to pending successfully');
      const updatedBids = bids.map(bid =>
        bid._id === bidId ? { ...bid, status: 'pending' } : bid
      );
      setBids(updatedBids);
    } catch (err) {
      console.error('Pending bid error:', err);
      toast.error('Failed to set bid to pending');
    }
  };

  const handleCompleteBid = async (bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'completed' });
      toast.success('Bid marked as completed successfully');
      const updatedBids = bids.map(bid =>
        bid._id === bidId ? { ...bid, status: 'completed' } : bid
      );
      setBids(updatedBids);
      setSelectedBidId(bidId);
      setShowFeedbackModal(true);
    } catch (err) {
      console.error('Complete bid error:', err);
      toast.error('Failed to mark bid as completed');
    }
  };

  const handleCancelBid = async (bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'canceled' });
      toast.success('Bid canceled successfully');
      const updatedBids = bids.map(bid =>
        bid._id === bidId ? { ...bid, status: 'canceled' } : bid
      );
      setBids(updatedBids);
    } catch (err) {
      console.error('Cancel bid error:', err);
      toast.error('Failed to cancel bid');
    }
  };

  const handleViewCollector = async (collectorId, bidStatus) => {
    if (!collectorId) {
      toast.error('Invalid collector ID');
      return;
    }
    if (bidStatus !== 'accepted') {
      toast.error('Collector details are only available for accepted bids.');
      return;
    }
    try {
      const endpoint = `/waste/collector-details/${collectorId}`;
      const data = await apiService.get(endpoint);
      setCollectorDetails(data);
      setIsCollectorModalOpen(true);
    } catch (err) {
      console.error('Error fetching collector details:', err);
      toast.error('Failed to load collector details');
    }
  };

  const handleChat = (collectorId) => {
    if (!collectorId) {
      toast.error('No chat available for this bid');
      return;
    }
    navigate(`/farmer/chat/farmer-waste-collector/${collectorId._id}`);
  };

  const handleSubmitFeedback = async () => {
    if (!feedback.rating || feedback.rating < 1 || feedback.rating > 5) {
      toast.error('Please provide a valid rating (1-5)');
      return;
    }
    try {
      const bid = bids.find(b => b._id === selectedBidId);
      if (!bid) return;

      await apiService.post(`/waste/feedback/${requestId}/${selectedBidId}`, {
        farmerRating: user.role === 'farmer' ? feedback.rating : undefined,
        collectorRating: user.role === 'farmer' ? undefined : feedback.rating,
        comments: feedback.comment,
      });
      toast.success('Feedback submitted successfully');
      setShowFeedbackModal(false);
      setFeedback({ rating: 0, comment: '' });
      setSelectedBidId(null);

      const data = await apiService.get(`/waste/bids/${requestId}`);
      setBids(data);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      toast.error('Failed to submit feedback');
    }
  };

  const handleViewReviews = (reviews) => {
    setSelectedReviews(reviews || []);
    setShowReviewModal(true);
  };

  if (!user || user.role !== 'farmer') {
    toast.error('Access denied. Farmers only.');
    return <Navigate to="/login" />;
  }

  return (
    <div className="p-5 dark:bg-gray-900 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
          Bids for Waste Request #{requestId}
        </h2>
        <button
          onClick={() => navigate('/farmer-waste-management')}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 transition duration-200"
        >
          Back to Requests
        </button>
      </div>
      {loading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md animate-pulse">Loading...</div>
      ) : error ? (
        <div className="card text-center text-red-600 dark:text-red-400 p-5 rounded-lg shadow-md">Error: {error.message}</div>
      ) : bids.length > 0 ? (
        <div className="card bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                <th className="p-4 border-b-2 border-gray-200 dark:border-gray-600 font-medium">Collector</th>
                <th className="p-4 border-b-2 border-gray-200 dark:border-gray-600 font-medium">Bid Amount ($)</th>
                <th className="p-4 border-b-2 border-gray-200 dark:border-gray-600 font-medium">Note</th>
                <th className="p-4 border-b-2 border-gray-200 dark:border-gray-600 font-medium">Rating</th>
                <th className="p-4 border-b-2 border-gray-200 dark:border-gray-600 font-medium">Reviews</th>
                <th className="p-4 border-b-2 border-gray-200 dark:border-gray-600 font-medium">Status</th>
                <th className="p-4 border-b-2 border-gray-200 dark:border-gray-600 font-medium">Created At</th>
                <th className="p-4 border-b-2 border-gray-200 dark:border-gray-600 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bids.map((bid) => (
                <tr key={bid._id} className="border-t dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-200">
                  <td className="p-4">
                    <button
                      onClick={() => handleViewCollector(bid.collectorId?._id || bid.collectorId, bid.status)}
                      className={`text-primary hover:text-opacity-90 underline ${bid.status !== 'accepted' ? 'cursor-not-allowed opacity-50' : ''}`}
                      title={bid.status !== 'accepted' ? 'Available after acceptance' : 'View Collector Details'}
                      disabled={bid.status !== 'accepted'}
                    >
                      {bid.collectorName || `${bid.collectorId?.firstName || ''} ${bid.collectorId?.lastName || ''}`}
                    </button>
                  </td>
                  <td className="p-4">{bid.amount || 'N/A'}</td>
                  <td className="p-4">{bid.note || 'N/A'}</td>
                  <td className="p-4">
                    {bid.rating ? (
                      <span className="flex items-center text-yellow-500">
                        {Array.from({ length: 5 }, (_, i) => (
                          <FaStar key={i} className={i < Math.round(bid.rating) ? 'text-yellow-500' : 'text-gray-300 dark:text-gray-500'} />
                        ))}
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
                          {bid.rating.toFixed(1)}
                        </span>
                      </span>
                    ) : 'N/A'}
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => handleViewReviews(bid.reviews)}
                      className="flex items-center px-2 py-1 bg-primary text-white rounded hover:bg-opacity-90 transition duration-200 disabled:opacity-50"
                      disabled={!bid.reviews || bid.reviews.length === 0}
                      title={bid.reviews && bid.reviews.length > 0 ? 'View Reviews' : 'No reviews available'}
                    >
                      <FaEye className="mr-1" /> {bid.reviews?.length || 0}
                    </button>
                  </td>
                  <td className="p-4">{bid.status || 'N/A'}</td>
                  <td className="p-4">{new Date(bid.createdAt).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}</td>
                  <td className="p-4 flex space-x-2">
                    {bid.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleAcceptBid(bid._id)}
                          className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition duration-200"
                          title="Accept Bid"
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => handleDeclineBid(bid._id)}
                          className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition duration-200"
                          title="Decline Bid"
                        >
                          Decline
                        </button>
                      </>
                    )}
                    {bid.status === 'accepted' && (
                      <>
                        <button
                          onClick={() => handleCompleteBid(bid._id)}
                          className="px-3 py-1 bg-primary text-white rounded hover:bg-opacity-90 transition duration-200"
                          title="Mark as Completed"
                        >
                          Completed
                        </button>
                        <button
                          onClick={() => handlePendingBid(bid._id)}
                          className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition duration-200"
                          title="Set to Pending"
                        >
                          Pending
                        </button>
                        <button
                          onClick={() => handleCancelBid(bid._id)}
                          className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition duration-200"
                          title="Cancel Bid"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleChat(bid.collectorId)}
                          className={`px-3 py-1 bg-primary text-white rounded hover:bg-opacity-90 transition duration-200 ${!bid.collectorId ? 'cursor-not-allowed opacity-50' : ''}`}
                          title={bid.collectorId ? 'Chat with Collector' : 'No chat available'}
                          disabled={!bid.collectorId}
                        >
                          <FaComment />
                        </button>
                      </>
                    )}
                    {bid.status === 'declined' && (
                      <button
                        onClick={() => handlePendingBid(bid._id)}
                        className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition duration-200"
                        title="Set to Pending"
                      >
                        Pending
                      </button>
                    )}
                    <button
                      onClick={() => handleViewCollector(bid.collectorId?._id || bid.collectorId, bid.status)}
                      className={`px-3 py-1 bg-primary text-white rounded hover:bg-opacity-90 transition duration-200 ${bid.status !== 'accepted' ? 'cursor-not-allowed opacity-50' : ''}`}
                      title={bid.status !== 'accepted' ? 'Available after acceptance' : 'View Collector'}
                      disabled={bid.status !== 'accepted'}
                    >
                      <FaUser />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md">
          No bids found for this request.
        </div>
      )}
      <Modal
        isOpen={isCollectorModalOpen}
        onClose={() => {
          setIsCollectorModalOpen(false);
          setCollectorDetails(null);
        }}
        title="Collector Details"
        className="max-w-lg mx-auto"
      >
        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          {loading ? (
            <p className="text-gray-600 dark:text-gray-300">Loading collector details...</p>
          ) : collectorDetails ? (
            <div className="space-y-4 text-gray-900 dark:text-gray-100">
              <p className="font-semibold"><strong>Name:</strong> {collectorDetails.firstName} {collectorDetails.lastName}</p>
              <p className="font-semibold"><strong>Email:</strong> {collectorDetails.email}</p>
              <p className="font-semibold"><strong>Phone:</strong> {collectorDetails.phone}</p>
              <p className="font-semibold"><strong>Address:</strong> {collectorDetails.address}</p>
              <p className="font-semibold"><strong>Street:</strong> {collectorDetails.street}</p>
              <p className="font-semibold"><strong>Postal Code:</strong> {collectorDetails.postalCode}</p>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-300">No details available.</p>
          )}
          <div className="flex justify-end mt-6">
            <button
              onClick={() => {
                setIsCollectorModalOpen(false);
                setCollectorDetails(null);
              }}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 transition duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showFeedbackModal}
        onClose={() => {
          setShowFeedbackModal(false);
          setFeedback({ rating: 0, comment: '' });
          setSelectedBidId(null);
        }}
        title="Provide Feedback"
        className="max-w-md mx-auto"
      >
        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Rate Your Experience</h3>
          <div className="space-y-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rating</label>
              <div className="flex items-center space-x-4">
                {Array.from({ length: 5 }, (_, i) => (
                  <FaStar
                    key={i}
                    className={`cursor-pointer text-2xl ${i < feedback.rating ? 'text-yellow-500' : 'text-gray-300 dark:text-gray-500'} hover:text-yellow-500 transition duration-200`}
                    onClick={() => setFeedback({ ...feedback, rating: i + 1 })}
                    onMouseEnter={() => setFeedback({ ...feedback, rating: i + 1 })}
                    onMouseLeave={() => setFeedback({ ...feedback, rating: feedback.rating || 0 })}
                  />
                ))}
                <span className="text-lg font-medium text-gray-600 dark:text-gray-300">{feedback.rating || '0'} / 5</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Comments</label>
              <textarea
                value={feedback.comment}
                onChange={(e) => setFeedback({ ...feedback, comment: e.target.value })}
                className="w-full p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-gray-100 text-base"
                rows="5"
                placeholder="Share your thoughts or feedback..."
              />
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowFeedbackModal(false);
                  setFeedback({ rating: 0, comment: '' });
                  setSelectedBidId(null);
                }}
                className="px-5 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitFeedback}
                className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 transition duration-200 disabled:opacity-50"
                disabled={!feedback.rating}
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setSelectedReviews([]);
        }}
        title="Collector Reviews"
        className="max-w-xl mx-auto"
      >
        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          {selectedReviews.length > 0 ? (
            <ul className="max-h-64 overflow-y-auto space-y-4 pr-2">
              {selectedReviews.map((review, index) => (
                <li key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm">
                  <p className="text-gray-900 dark:text-gray-100"><strong>Rating:</strong> {review.rating} / 5</p>
                  <p className="text-gray-700 dark:text-gray-300 mt-1"><strong>Comment:</strong> "{review.comment}"</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    <strong>Date:</strong> {new Date(review.createdAt).toLocaleDateString()}
                  </p>
                  {review.relatedRequestId && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      <strong>Related Request:</strong> {review.relatedRequestId}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 dark:text-gray-300 text-center">No reviews available.</p>
          )}
          <div className="flex justify-end mt-6">
            <button
              onClick={() => {
                setShowReviewModal(false);
                setSelectedReviews([]);
              }}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 transition duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default BidListPage;